/* eslint-disable react/prop-types */
import React from 'react';
import { Table } from 'antd';

import EmptyState from './EmptyState';
import { useStickyMetrics } from './stickyLayout';
import styles from './DataTable.less';

/**
 * DataTable —— 全模块统一的表格壳
 * ------------------------------------------------------------------
 * 取代各页散落的裸 Table / ProTable，把这几件事一次做对：
 *   1. 表头吸顶（滚动时列名不丢）；
 *   2. 数值列右对齐 + 等宽数字（列不跳动，方案 §3.2）；
 *   3. 四态覆盖：首次加载出骨架（保留表头）；已有数据但刷新中出轻遮罩；
 *      无数据出空态；正常出数据；
 *   4. 分页默认值统一（小尺寸、可换每页条数、右下角总数）。
 *
 * 列扩展字段（本壳支持，业务侧优先用它而不是自己写 render）：
 *   numeric  数字列 → 右对齐 + tabular-nums
 *   unit     单位（浅色小字后缀，如「节」「元」）
 *   format   取值格式化（建议用 utils/format.js 里的函数）
 *   empty    该列空值占位，默认 '--'（不要用 0 糊，见 M1 §12.2）
 *
 * @param columns       列定义（见上）
 * @param dataSource    数据
 * @param rowKey        行键，默认 `id`
 * @param loading       加载中；无数据时出骨架屏
 * @param emptyText     空态主文案
 * @param emptyHint     空态补充说明（例如口径或下一步）
 * @param emptyAction   空态动作节点（如「查看历史数据」按钮）
 * @param pagination    透传 antd；默认小尺寸 + 总数，传 false 关闭
 * @param sticky        表头吸顶，默认开；传对象可透传 antd 的 sticky 配置
 * @param stickyOffset  吸顶偏移（px）；不传则按「应用顶栏 + 页面吸顶头」实测高度计算
 *                      （见 ui/stickyLayout.js，用写死值会错位）
 * @param onChange      透传 antd Table 的 onChange —— **受控服务端排序/分页必需**：
 *                      需要时在列上报 `sorter` + `sortOrder`，此处取
 *                      `(pagination, filters, sorter)` 再回写请求参数
 *                      （本地 comparator 只能排当页，跨页会错）。
 *                      2026-09-17 补：此前没透传，会员经营的看得见活动只能继续用裸 Table。
 */
const DataTable = (props) => {
  const {
    columns = [],
    dataSource,
    rowKey = 'id',
    loading,
    emptyText = '暂无数据',
    emptyHint,
    emptyAction,
    pagination,
    onRow,
    size = 'middle',
    scroll,
    sticky = true,
    stickyOffset,
    className,
    rowClassName,
    tableLayout,
    footer,
    onChange,
  } = props;

  const rows = dataSource || [];
  const stickyMetrics = useStickyMetrics();
  const offsetHeader =
    stickyOffset === undefined ? stickyMetrics.tableOffset : stickyOffset;
  const firstLoading = Boolean(loading) && rows.length === 0;
  const isEmpty = !loading && rows.length === 0;

  /* 列归一化：数值列统一右对齐 + 等宽；unit/format 组合成默认渲染。
     用 memo 降低重建频率——注意：若调用方把列定义写成内联数组字面量，
     每次渲染引用都会变，memo 就失效了；列定义请放模块作用域或自行 memo。 */
  const normColumns = React.useMemo(
    () =>
      columns.map((col) => {
        if (!col) return col;

        let next = col;
        const numeric = col.numeric === true || col.align === 'right';

        if (col.empty === undefined) next = { ...next, empty: '--' };

        if (!col.render && (col.format || col.unit)) {
          const { format, unit } = col;
          next = {
            ...next,
            render: (value) => {
              const text = format ? format(value) : value;
              const blank = text === undefined || text === null || text === '';
              if (blank)
                return (
                  <span className={styles.blank}>{col.empty || '--'}</span>
                );
              return (
                <span className={numeric ? styles.numCell : undefined}>
                  {text}
                  {unit ? <span className={styles.unit}>{unit}</span> : null}
                </span>
              );
            },
          };
        }

        if (numeric) {
          next = {
            ...next,
            align: 'right',
            className: `${styles.num} ${next.className || ''}`,
          };
        }

        return next;
      }),
    [columns],
  );

  /* 骨架屏：保留表头，行内用灰条占位（形状贴合真实内容） */
  const skeleton = (
    <div className={styles.skeleton}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div className={styles.skeletonRow} key={i}>
          {normColumns.slice(0, 6).map((col, j) => (
            <span
              className={styles.skeletonBar}
              key={(col && col.key) || (col && col.dataIndex) || j}
              style={{ width: j === 0 ? '28%' : '14%' }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  const emptyNode = isEmpty ? (
    <EmptyState
      description={emptyText}
      hint={emptyHint}
      action={emptyAction}
      compact
    />
  ) : (
    skeleton
  );

  const paginationConfig =
    pagination === undefined
      ? {
          size: 'small',
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }
      : pagination;

  /* 空态分页器：**只在自己兜底时**隐藏——调用方显式传了 pagination
     （例如空态仍要展示总数）就尊重调用方，不静默覆盖。 */
  const finalPagination =
    isEmpty && pagination === undefined ? false : paginationConfig;

  return (
    <div className={`${styles.wrap} ${className || ''}`}>
      <Table
        columns={normColumns}
        dataSource={rows}
        rowKey={rowKey}
        size={size}
        locale={{ emptyText: firstLoading || isEmpty ? emptyNode : '暂无数据' }}
        loading={Boolean(loading) && rows.length > 0}
        pagination={finalPagination}
        onRow={onRow}
        scroll={scroll}
        sticky={sticky === true ? { offsetHeader } : sticky}
        rowClassName={rowClassName}
        tableLayout={tableLayout}
        footer={footer}
        onChange={onChange}
      />
    </div>
  );
};

export default DataTable;
